import type { CustomerReviewStatus as CustomerReviewStatusValue } from '@/lib/services/customer-reviews/customer-reviews.types'
import type { BadgeProps } from '@/components/ui/badge'
import type { NumericInput } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { CustomerReviewStatus } from '@/lib/enums/CustomerReviewStatus'
import { cn } from '@/lib/utils'
import { Star } from 'lucide-react'

export const CUSTOMER_REVIEW_IGNORED_TOOLTIP =
  'Ignored reviews stay private and are not included in your average review score.'

export function getCustomerReviewStatusBadgeVariant(
  status: CustomerReviewStatusValue | null | undefined
): BadgeProps['variant'] {
  switch (status) {
    case CustomerReviewStatus.Published.Value:
      return 'success'
    case CustomerReviewStatus.Rejected.Value:
      return 'destructive'
    case CustomerReviewStatus.Hidden.Value:
      return 'muted'
    case CustomerReviewStatus.Pending.Value:
    case null:
    case undefined:
    default:
      return 'warning'
  }
}

export function getCustomerReviewStatusLabel(
  status: CustomerReviewStatusValue | null | undefined
): string {
  switch (status) {
    case CustomerReviewStatus.Pending.Value:
      return 'Pending'
    case CustomerReviewStatus.Published.Value:
      return 'Public'
    case CustomerReviewStatus.Hidden.Value:
      return 'Hidden'
    case CustomerReviewStatus.Rejected.Value:
      return 'Ignored'
    case null:
    case undefined:
    default:
      return status || 'Review'
  }
}

export function CustomerReviewStatusBadge({
  status,
  className,
}: {
  status?: CustomerReviewStatusValue | null
  className?: string
}) {
  const tooltip =
    status === CustomerReviewStatus.Rejected.Value ? CUSTOMER_REVIEW_IGNORED_TOOLTIP : undefined

  return (
    <Badge
      variant={getCustomerReviewStatusBadgeVariant(status)}
      className={className}
      title={tooltip}
    >
      {getCustomerReviewStatusLabel(status)}
    </Badge>
  )
}

export function CustomerReviewStars({
  rating,
  className,
  iconClassName = 'size-4',
}: {
  rating?: NumericInput
  className?: string
  iconClassName?: string
}) {
  const normalizedRating = Number(rating) || 0

  return (
    <span
      className={cn('inline-flex items-center justify-center gap-0.5 text-interaction', className)}
      aria-label={`${normalizedRating} star review`}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            iconClassName,
            star <= normalizedRating ? 'fill-primary text-interaction' : 'text-muted-foreground/35'
          )}
        />
      ))}
      <span className="sr-only">{normalizedRating} star review</span>
    </span>
  )
}
